import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import VendorDocumentsSection from "./VendorDocumentsSection";
import AddBankDialog from "./AddBankDialog";
import { upsertVendorOption } from "../paymentDesk/shared/refDataStore";

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_RE = /^[0-9]{9,18}$/;

const BANK_MANAGE_ROLES = ["Director", "Manager", "Back Office"];
const ADD_BANK = "__add_bank__";

// Session cache for the form's pick-lists (shown instantly, quietly refreshed)
// and short-lived vendor-record prefetch (hover/click) so the form opens
// after at most one round trip.
const lookups = { categories: [], banks: [] };
const prefetched = new Map();

export function warmVendorLookups() {
  client.get("/vendor-master/categories").then((res) => { lookups.categories = res.data; }).catch(() => {});
  client.get("/configurations/banks").then((res) => { lookups.banks = res.data; }).catch(() => {});
}

export function prefetchVendor(code) {
  const hit = prefetched.get(code);
  if (hit && Date.now() - hit.at < 15000) return hit.promise;
  const promise = client.get(`/vendor-master/vendors/${code}`).then((res) => res.data);
  promise.catch(() => prefetched.delete(code));
  prefetched.set(code, { at: Date.now(), promise });
  return promise;
}

const clean = (v) => v.replace(/\s+/g, "").toUpperCase();

// Vendor Master — Vendor form. `vendor_code` is system-generated and
// frozen at creation (VEN-<CAT>-<YEAR>-####) — read-only in the UI even
// on edit. PAN/GSTIN/IFSC/Account Number carry strict format checks +
// GSTIN/phone hard-block duplicates + a soft name-duplicate warning (Aug
// 2026) — see payment_desk_vendors.py for the matching backend rules.
// `readOnly` disables the whole form (Accounts viewing an existing vendor).
function VendorFormDialogInner({ vendor, readOnly, onClose, onSaved }) {
  const isEdit = !!vendor;
  const { user } = useAuth();
  const canAddBank = BANK_MANAGE_ROLES.includes(user?.role);
  const [banks, setBanks] = useState(lookups.banks);
  const [addBankOpen, setAddBankOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(null); // {done, total} while attaching staged docs post-create
  const [categories, setCategories] = useState(lookups.categories);
  const [documents, setDocuments] = useState(vendor?.documents || []);
  const [staged, setStaged] = useState([]);
  const [errors, setErrors] = useState({});
  const [nameWarning, setNameWarning] = useState(null);
  const [form, setForm] = useState({
    vendor_name: vendor?.vendor_name || "",
    category_id: vendor?.category_id || "",
    gstin: vendor?.gstin || "",
    pan: vendor?.pan || "",
    account_number: vendor?.bank_details?.account_number || "",
    ifsc: vendor?.bank_details?.ifsc || "",
    account_holder_name: vendor?.bank_details?.account_holder_name || "",
    bank_name: vendor?.bank_details?.bank_name || "",
    contact_person: vendor?.contact_person || "",
    phone: vendor?.phone || "",
  });

  useEffect(() => {
    client.get("/vendor-master/categories").then((res) => { lookups.categories = res.data; setCategories(res.data); }).catch(() => {});
    client.get("/configurations/banks").then((res) => { lookups.banks = res.data; setBanks(res.data); }).catch(() => {});
  }, []);

  const bankOptions = () => {
    const opts = banks.map((b) => ({ value: b.name, label: b.name }));
    if (form.bank_name && !banks.some((b) => b.name === form.bank_name)) opts.unshift({ value: form.bank_name, label: form.bank_name });
    if (form.bank_name) opts.push({ value: "", label: "Clear selection", pinned: true, testId: "vendor-form-bank-option-clear" });
    if (canAddBank) opts.push({ value: ADD_BANK, label: "+ Add new bank", pinned: true, className: "font-semibold text-amber-700", testId: "vendor-form-bank-option-add-new" });
    return opts;
  };

  const onBankChange = (v) => (v === ADD_BANK ? setAddBankOpen(true) : set("bank_name", v));

  const onBankAdded = (bank) => {
    setBanks((bs) => [...bs, bank].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())));
    set("bank_name", bank.name);
    setAddBankOpen(false);
  };

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setError = (key, msg) => setErrors((e) => ({ ...e, [key]: msg || undefined }));

  const refreshDocuments = () => {
    client.get(`/vendor-master/vendors/${vendor.vendor_code}`).then((res) => setDocuments(res.data.documents)).catch(() => {});
  };

  const checkDuplicate = async (field, value) => {
    if (!value) return setError(`${field}_duplicate`, null);
    try {
      const res = await client.get("/vendor-master/vendors/check-duplicate", {
        params: { field, value, exclude_code: vendor?.vendor_code || undefined },
      });
      const dup = res.data.duplicate;
      if (!dup) {
        setError(`${field}_duplicate`, null);
        if (field === "name") setNameWarning(null);
        return;
      }
      const who = `${dup.vendor_code} — ${dup.vendor_name}${dup.deactivated ? " (deactivated)" : ""}`;
      if (field === "name") {
        setNameWarning(`A vendor with a very similar name already exists: ${who}`);
      } else {
        setError(`${field}_duplicate`, `${field === "gstin" ? "GSTIN" : "Phone number"} already registered to ${who}`);
      }
    } catch {
      // non-blocking — backend still enforces at save
    }
  };

  const checkGstinPanCross = (gstin, pan) => {
    if (gstin && GSTIN_RE.test(gstin) && pan && PAN_RE.test(pan) && gstin.slice(2, 12) !== pan) {
      setError("cross", "PAN does not match the PAN embedded in the GSTIN");
    } else {
      setError("cross", null);
    }
  };

  const onBlurGstin = () => {
    const g = form.gstin;
    if (!g) return setError("gstin", null);
    if (!GSTIN_RE.test(g)) return setError("gstin", "GSTIN format is invalid — expected format 22AAAAA0000A1Z5");
    setError("gstin", null);
    let pan = form.pan;
    if (!pan) {
      pan = g.slice(2, 12);
      set("pan", pan);
    }
    checkGstinPanCross(g, pan);
    checkDuplicate("gstin", g);
  };

  const onBlurPan = () => {
    const p = form.pan;
    if (!p) return setError("pan", null);
    if (!PAN_RE.test(p)) return setError("pan", "PAN format is invalid — expected format AAAAA1234A");
    setError("pan", null);
    checkGstinPanCross(form.gstin, p);
  };

  const onBlurIfsc = () => {
    if (!form.ifsc) return setError("ifsc", null);
    setError("ifsc", IFSC_RE.test(form.ifsc) ? null : "IFSC format is invalid — expected format ABCD0123456");
  };

  const onBlurAccountNumber = () => {
    if (!form.account_number) return setError("account_number", null);
    setError("account_number", ACCOUNT_RE.test(form.account_number) ? null : "Account Number must be 9-18 digits");
  };

  const hasBlockingErrors = () =>
    Object.values(errors).some(Boolean) ||
    (form.gstin && !GSTIN_RE.test(form.gstin)) ||
    (form.pan && !PAN_RE.test(form.pan)) ||
    (form.ifsc && !IFSC_RE.test(form.ifsc)) ||
    (form.account_number && !ACCOUNT_RE.test(form.account_number));

  const uploadStaged = async (vendorCode, files) => {
    setUploadingDocs({ done: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append("doc_type", files[i].doc_type);
      fd.append("file", files[i].file);
      try {
        await client.post(`/vendor-master/vendors/${vendorCode}/documents`, fd);
      } catch (err) {
        toast.error(`${files[i].file.name}: ${getErrorMessage(err)}`);
      }
      setUploadingDocs({ done: i + 1, total: files.length });
    }
    setUploadingDocs(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vendor_name.trim()) return toast.error("Vendor Name is required");
    if (!form.category_id) return toast.error("Vendor Category is required");
    if (hasBlockingErrors()) return toast.error("Fix the highlighted errors before saving");
    setSaving(true);
    const payload = {
      vendor_name: form.vendor_name.trim(),
      category_id: form.category_id,
      gstin: form.gstin.trim() || null,
      pan: form.pan.trim() || null,
      bank_details: {
        account_number: form.account_number.trim() || null,
        ifsc: form.ifsc.trim() || null,
        account_holder_name: form.account_holder_name.trim() || null,
        bank_name: form.bank_name.trim() || null,
      },
      contact_person: form.contact_person.trim() || null,
      phone: form.phone.trim() || null,
    };
    try {
      if (isEdit) {
        const res = await client.put(`/vendor-master/vendors/${vendor.vendor_code}`, payload);
        toast.success("Vendor updated");
        upsertVendorOption(res.data);
        prefetched.delete(vendor.vendor_code);
        onSaved(res.data);
      } else {
        const res = await client.post("/vendor-master/vendors", payload);
        if (staged.length) await uploadStaged(res.data.vendor_code, staged);
        toast.success(`Vendor created — ${res.data.vendor_code}`);
        upsertVendorOption(res.data);
        onSaved(res.data);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="vendor-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `${readOnly ? "Vendor" : "Edit Vendor"} — ${vendor.vendor_code}` : "Add Vendor"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {isEdit && (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Vendor Code</label>
              <Input data-testid="vendor-form-code-display" value={vendor.vendor_code} disabled className="font-mono text-xs" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Vendor Name *</label>
              <Input
                data-testid="vendor-form-name-input"
                value={form.vendor_name}
                onChange={(e) => set("vendor_name", e.target.value)}
                onBlur={() => checkDuplicate("name", form.vendor_name.trim())}
                required
                disabled={readOnly}
              />
              {nameWarning && (
                <p className="text-xs text-amber-600 mt-1" data-testid="vendor-form-name-warning">{nameWarning}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Vendor Category *</label>
              <SearchableSelect testId="vendor-form-category-select" value={form.category_id} onValueChange={(v) => set("category_id", v)} disabled={readOnly} placeholder="Select category"
  options={categories.map((c) => ({ value: c.id, label: `${c.name} (${c.code})`, testId: `vendor-form-category-option-${c.code}` }))} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">GSTIN</label>
              <Input
                data-testid="vendor-form-gstin-input"
                value={form.gstin}
                onChange={(e) => set("gstin", clean(e.target.value))}
                onBlur={onBlurGstin}
                maxLength={15}
                placeholder="22AAAAA0000A1Z5"
                disabled={readOnly}
              />
              {(errors.gstin || errors.gstin_duplicate || errors.cross) && (
                <p className="text-xs text-red-600 mt-1" data-testid="vendor-form-gstin-error">{errors.gstin || errors.gstin_duplicate || errors.cross}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">PAN</label>
              <Input
                data-testid="vendor-form-pan-input"
                value={form.pan}
                onChange={(e) => set("pan", clean(e.target.value))}
                onBlur={onBlurPan}
                maxLength={10}
                placeholder="AAAAA1234A"
                disabled={readOnly}
              />
              {errors.pan && <p className="text-xs text-red-600 mt-1" data-testid="vendor-form-pan-error">{errors.pan}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Contact Person</label>
              <Input data-testid="vendor-form-contact-person-input" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} disabled={readOnly} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone</label>
              <Input
                data-testid="vendor-form-phone-input"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                onBlur={() => checkDuplicate("phone", form.phone.trim())}
                maxLength={12}
                disabled={readOnly}
              />
              {errors.phone_duplicate && <p className="text-xs text-red-600 mt-1" data-testid="vendor-form-phone-error">{errors.phone_duplicate}</p>}
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
              Bank Details — required before any RTGS/NEFT line to this vendor can be marked Paid
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Account Number</label>
                <Input
                  data-testid="vendor-form-account-number-input"
                  value={form.account_number}
                  onChange={(e) => set("account_number", e.target.value.replace(/\s+/g, ""))}
                  onBlur={onBlurAccountNumber}
                  placeholder="e.g. 123456789012"
                  disabled={readOnly}
                />
                {errors.account_number && <p className="text-xs text-red-600 mt-1" data-testid="vendor-form-account-number-error">{errors.account_number}</p>}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">IFSC</label>
                <Input
                  data-testid="vendor-form-ifsc-input"
                  value={form.ifsc}
                  onChange={(e) => set("ifsc", clean(e.target.value))}
                  onBlur={onBlurIfsc}
                  maxLength={11}
                  placeholder="ABCD0123456"
                  disabled={readOnly}
                />
                {errors.ifsc && <p className="text-xs text-red-600 mt-1" data-testid="vendor-form-ifsc-error">{errors.ifsc}</p>}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Account Holder Name</label>
                <Input data-testid="vendor-form-account-holder-input" value={form.account_holder_name} onChange={(e) => set("account_holder_name", e.target.value)} disabled={readOnly} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Bank Name</label>
                <SearchableSelect testId="vendor-form-bank-name-select" value={form.bank_name} onValueChange={onBankChange} disabled={readOnly} placeholder="Select bank" options={bankOptions()} />
              </div>
            </div>
          </div>

          <VendorDocumentsSection
            vendorCode={vendor?.vendor_code}
            documents={documents}
            canManage={!readOnly}
            onUploaded={refreshDocuments}
            onDeleted={refreshDocuments}
            staged={staged}
            onStagedChange={setStaged}
          />
          {uploadingDocs && (
            <p className="text-xs text-amber-600" data-testid="vendor-form-doc-upload-progress">
              Uploading documents... ({uploadingDocs.done}/{uploadingDocs.total})
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="vendor-form-cancel-button">{readOnly ? "Close" : "Cancel"}</Button>
            {!readOnly && (
              <Button type="submit" disabled={saving} data-testid="vendor-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
                {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Vendor"}
              </Button>
            )}
          </DialogFooter>
        </form>
        {addBankOpen && <AddBankDialog onClose={() => setAddBankOpen(false)} onAdded={onBankAdded} />}
      </DialogContent>
    </Dialog>
  );
}

// List rows are lightweight (no bank numbers/documents), so an existing
// vendor's full record is fetched when the form opens.
export default function VendorFormDialog({ vendor, ...rest }) {
  const [full, setFull] = useState(null);
  const code = vendor?.vendor_code;
  useEffect(() => {
    if (!code) return;
    prefetchVendor(code).then((data) => { prefetched.delete(code); setFull(data); }).catch((err) => { toast.error(getErrorMessage(err)); rest.onClose(); });
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!vendor) return <VendorFormDialogInner vendor={null} {...rest} />;
  if (!full) {
    return (
      <Dialog open onOpenChange={(open) => !open && rest.onClose()}>
        <DialogContent className="max-w-2xl" data-testid="vendor-form-loading">
          <DialogHeader><DialogTitle>{vendor.vendor_code}</DialogTitle></DialogHeader>
          <p className="py-8 text-center text-sm text-slate-400">Loading…</p>
        </DialogContent>
      </Dialog>
    );
  }
  return <VendorFormDialogInner vendor={full} {...rest} />;
}
